/**
 * Поиск по списку кампаний: по названию, номеру кампании и артикулу.
 *
 * ЗАЧЕМ ЭТО НУЖНО, ЕСЛИ ЕСТЬ ОТБОР И СОРТИРОВКА. Отбор отвечает
 * на вопрос «какие кампании такие-то», а поиск — на другой, и куда более
 * частый: «где вот эта». Человек приходит с готовым ответом в голове —
 * с артикулом из отчёта, с номером кампании из письма Wildberries,
 * с обрывком названия — и ему нужно не сравнить тридцать шесть строк,
 * а найти одну. Прокруткой это делается взглядом по всему списку,
 * то есть медленно и с промахами.
 *
 * ТРИ ПОЛЯ СРАЗУ, А НЕ ВЫБОР «ИСКАТЬ ПО…». Выпадающий список «по чему
 * искать» — лишнее решение перед каждым поиском, причём решение, которое
 * человек примет неверно и решит, что кампании нет. Числа ищутся среди
 * номеров, буквы — среди названий, и различать это по виду запроса
 * умеем мы, а не он.
 *
 * АРТИКУЛ ИЩЕТСЯ ПО ВСЕМУ СОСТАВУ, А НЕ ПО ЧЕТЫРЁМ ФОТОГРАФИЯМ.
 * До этого сервер отдавал по четыре номера на кампанию — ровно столько,
 * сколько помещается картинок в строку. Поиск по такому списку находил бы
 * товар в кампании из трёх и не находил в кампании из тридцати, МОЛЧА:
 * пустой ответ выглядит как «такого артикула нет в рекламе» — самый
 * дорогой из возможных неверных ответов.
 *
 * ЗДЕСЬ ТОЛЬКО РАЗБОР. Ни сети, ни разметки.
 */

export interface Searchable {
  name: string | null;
  advertId: number;
  /** Весь состав кампании, а не только показанные фотографии. */
  nmIds: number[];
}

/**
 * Запрос к сравнимому виду: без лишних пробелов, в нижнем регистре.
 *
 * ПРОБЕЛЫ ПО КРАЯМ ОТРЕЗАЮТСЯ ВСЕГДА. Артикул, скопированный из таблицы,
 * приходит с пробелом или переводом строки на конце — и точное совпадение
 * по номеру не сработало бы ни разу, а человек решил бы, что товара
 * в рекламе нет.
 */
export function normalize(query: string): string {
  return query.trim().toLowerCase();
}

/**
 * Похоже ли на номер: только цифры.
 *
 * ОТ ЭТОГО ЗАВИСИТ СТРОГОСТЬ СРАВНЕНИЯ. Название ищем вхождением —
 * «сереб» найдёт «Серебро 925». Номера вхождением искать нельзя:
 * запрос «226» нашёл бы и 32440226, и 22600001, и половину кабинета
 * заодно. Поэтому у чисел сравнение с начала номера, а точное совпадение
 * ставится выше любого другого.
 */
function digitsOnly(q: string): boolean {
  return q.length > 0 && /^\d+$/.test(q);
}

/**
 * Подходит ли кампания под запрос.
 *
 * ПУСТОЙ ЗАПРОС ПОДХОДИТ ВСЕМ. Иначе очистка поля прятала бы весь список
 * вместо того, чтобы его вернуть.
 */
export function matchesQuery(item: Searchable, query: string): boolean {
  const q = normalize(query);
  if (q === '') return true;

  if (digitsOnly(q)) {
    // Номер кампании: точное совпадение либо начало. «3908» найдёт
    // 39086348 — ровно так номер и вспоминают, по первым цифрам.
    const advert = String(item.advertId);
    if (advert === q || advert.startsWith(q)) return true;

    // Артикул товара: то же правило, но по всему составу кампании.
    for (const nm of item.nmIds) {
      const s = String(nm);
      if (s === q || s.startsWith(q)) return true;
    }

    // ЧИСЛО ИЩЕТСЯ И В НАЗВАНИИ ТОЖЕ. «Кампания от 08.07.2026» —
    // название, состоящее из чисел; запрет искать цифры в тексте
    // сделал бы такие кампании ненаходимыми вовсе.
    return (item.name ?? '').toLowerCase().includes(q);
  }

  return (item.name ?? '').toLowerCase().includes(q);
}

/** Сколько строк подходит — для подписи «ничего не найдено». */
export function search<T extends Searchable>(items: readonly T[], query: string): T[] {
  const q = normalize(query);
  if (q === '') return [...items];
  return items.filter((i) => matchesQuery(i, q));
}
